import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, Box, Card, CardContent, LinearProgress } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrackChangesOutlined, LocalHospitalOutlined, CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import StatCard from '../components/StatCard';
import { getEngagement } from '../utils/api';

const TT = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8 };

function RateGauge({ label, value, color, icon }) {
  const pct = parseFloat(value) || 0;
  return (
    <Box sx={{ textAlign:'center', p:2, bgcolor:'rgba(255,255,255,0.03)', borderRadius:2 }}>
      <Box sx={{ color, mb:1 }}>{icon}</Box>
      <Typography variant="h4" fontFamily='"IBM Plex Mono",monospace' fontWeight={700} color={color}>{pct}%</Typography>
      <LinearProgress variant="determinate" value={Math.min(pct,100)}
        sx={{ mt:1, mb:1, height:6, borderRadius:3, bgcolor:'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar':{ bgcolor:color, borderRadius:3 } }}/>
      <Typography variant="caption" color="text.secondary" lineHeight={1.3} display="block">{label}</Typography>
    </Box>
  );
}

export default function EngagementPage() {
  const [data,    setData]    = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getEngagement(filters);
      setData(r.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const byRegion = data?.byRegion || [];
  const regionBar = byRegion.map(r => ({
    region: r.region,
    Responded:      r.responded,
    Attended:       r.attended,
    'Completed Flow': r.completedFlow,
    'Not Responding': r.notResponding,
  }));
  const rateBar = byRegion.map(r => ({
    region: r.region,
    'Response Rate':   parseFloat(r.responseRate),
    'Attendance Rate': parseFloat(r.attendanceRate),
    'Flow Rate':       parseFloat(r.flowRate),
  }));

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={700} color="primary.main">Patient Engagement</Typography>
        <Typography variant="body2" color="text.secondary">Appointment response · Hospital attendance · Flow completion</Typography>
      </Box>

      <FilterBar filters={filters} onChange={setFilters} showGroup={false} />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={3}><StatCard title="Total Patients"   value={data?.total?.toLocaleString()||'—'} icon={<TrackChangesOutlined/>}  color="#00BFA5" loading={loading}/></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Responded"        value={data?.responded?.toLocaleString()||'—'} icon={<CheckCircleOutlined/>} color="#40C4FF" loading={loading}/></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Attended Hospital" value={data?.attended?.toLocaleString()||'—'} icon={<LocalHospitalOutlined/>} color="#69F0AE" loading={loading}/></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Not Responding"   value={data?.notResponding?.toLocaleString()||'—'} icon={<WarningAmberOutlined/>} color="#FF5252" loading={loading}/></Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        {[
          { label:'Appointment Response Rate', value: data?.total>0 ? ((data.responded/data.total)*100).toFixed(1):'0', color:'#40C4FF', icon:<TrackChangesOutlined/> },
          { label:'Hospital Attendance Rate',  value: data?.total>0 ? ((data.attended/data.total)*100).toFixed(1):'0',  color:'#69F0AE', icon:<LocalHospitalOutlined/> },
          { label:'Flow Completion Rate',      value: data?.total>0 ? ((data.completedFlow/data.total)*100).toFixed(1):'0', color:'#CE93D8', icon:<CheckCircleOutlined/> },
          { label:'Non-Response Rate',         value: data?.total>0 ? ((data.notResponding/data.total)*100).toFixed(1):'0', color:'#FF5252', icon:<WarningAmberOutlined/> },
        ].map(item => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Card><CardContent sx={{ p:2 }}><RateGauge {...item}/></CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Engagement Counts by Region</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={regionBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="region" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis tick={{ fontSize:11, fill:'#90A4AE' }}/>
                  <Tooltip contentStyle={TT}/><Legend iconSize={10} wrapperStyle={{ fontSize:11 }}/>
                  <Bar dataKey="Responded"       fill="#40C4FF" radius={[3,3,0,0]}/>
                  <Bar dataKey="Attended"        fill="#69F0AE" radius={[3,3,0,0]}/>
                  <Bar dataKey="Completed Flow"  fill="#CE93D8" radius={[3,3,0,0]}/>
                  <Bar dataKey="Not Responding"  fill="#FF5252" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Engagement Rates (%) by Region</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rateBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="region" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis domain={[0,100]} tick={{ fontSize:11, fill:'#90A4AE' }} unit="%"/>
                  <Tooltip contentStyle={TT} formatter={v => `${v}%`}/><Legend iconSize={10} wrapperStyle={{ fontSize:11 }}/>
                  <Bar dataKey="Response Rate"   fill="#40C4FF" radius={[3,3,0,0]}/>
                  <Bar dataKey="Attendance Rate" fill="#69F0AE" radius={[3,3,0,0]}/>
                  <Bar dataKey="Flow Rate"       fill="#CE93D8" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
